<template>
  <div>
    <draggable
      v-model="ingredients"
      handle=".drag-handle"
      ghostClass="drag-ghost"
      dragClass="drag-item"
      @end="onReorder">
      <div
        v-for="(ingredient, index) in ingredients"
        :key="ingredient.id"
        class="box mb-3 ingredient-item">
        <div class="columns is-vcentered">
          <!-- Drag handle -->
          <div class="column is-narrow">
            <span class="drag-handle" style="cursor: grab;">
              <b-icon icon="drag-vertical" type="is-grey-light"></b-icon>
            </span>
          </div>

          <!-- Icon preview -->
          <div class="column is-narrow">
            <div
              class="icon-preview"
              :style="{ backgroundColor: ingredient.icon_color || '#7CB342' }">
              <img v-if="ingredient.icon_url" :src="ingredient.icon_url" alt="" />
              <b-icon v-else icon="leaf" size="is-medium" type="is-white" />
            </div>
          </div>

          <!-- Name (read-only from selected ingredient) -->
          <div class="column">
            <strong>{{ ingredient.name }}</strong>
            <p class="has-text-grey is-size-7" v-if="ingredient.search_term && ingredient.search_term !== ingredient.name">
              Search: {{ ingredient.search_term }}
            </p>
          </div>

          <!-- Override search term (optional) -->
          <div class="column is-3">
            <b-field label="Search term override" label-position="on-border">
              <b-input
                v-model="ingredient.search_term"
                :placeholder="ingredient.name"
                size="is-small"
                @input="emitUpdate"
              />
            </b-field>
          </div>

          <!-- Delete button -->
          <div class="column is-narrow">
            <b-button
              type="is-danger"
              icon-right="delete"
              size="is-small"
              @click="removeIngredient(index)"
            />
          </div>
        </div>
      </div>
    </draggable>

    <!-- Add new ingredient -->
    <div class="mt-4">
      <b-field>
        <b-autocomplete
          ref="ingredientSearch"
          v-model="searchQuery"
          :data="filteredIngredientsList"
          placeholder="Search ingredients to add..."
          icon="magnify"
          field="name"
          :loading="isLoadingIngredients"
          @select="selectIngredient"
          :open-on-focus="true"
          :clearable="true">
          <template #empty>No ingredients found</template>
          <template slot-scope="props">
            <div class="is-flex is-align-items-center">
              <div
                class="icon-preview-small mr-2"
                :style="{ backgroundColor: props.option.icon_color || '#7CB342' }">
                <img v-if="props.option.icon_url" :src="props.option.icon_url" alt="" />
                <b-icon v-else icon="leaf" size="is-small" type="is-white" />
              </div>
              <span>{{ props.option.name }}</span>
              <span v-if="isAlreadySelected(props.option.id)" class="ml-2 has-text-grey is-size-7">(already added)</span>
            </div>
          </template>
        </b-autocomplete>
      </b-field>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import db from '@/firebase/db'

export default {
  name: 'EditSeasonalIngredientsList',
  components: {
    Draggable
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    monthId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      allIngredients: [],
      isLoadingIngredients: true
    }
  },
  computed: {
    ingredients: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    filteredIngredientsList() {
      if (!this.searchQuery) {
        return this.allIngredients.slice(0, 20) // Show first 20 when no search
      }
      const query = this.searchQuery.toLowerCase()
      return this.allIngredients.filter(ing =>
        ing.name.toLowerCase().includes(query)
      ).slice(0, 20)
    }
  },
  created() {
    this.fetchIngredients()
  },
  methods: {
    async fetchIngredients() {
      this.isLoadingIngredients = true
      try {
        const snapshot = await db.collection('ingredients').orderBy('name').get()
        this.allIngredients = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('Error fetching ingredients:', error)
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Failed to load ingredients'
        })
      }
      this.isLoadingIngredients = false
    },
    emitUpdate() {
      this.$emit('input', [...this.ingredients])
    },
    onReorder() {
      this.emitUpdate()
    },
    isAlreadySelected(ingredientId) {
      return this.ingredients.some(ing => ing.ingredient_id === ingredientId)
    },
    selectIngredient(ingredient) {
      if (!ingredient) return

      // Check if already added
      if (this.isAlreadySelected(ingredient.id)) {
        this.$buefy.toast.open({
          type: 'is-warning',
          message: `${ingredient.name} is already in the list`
        })
        this.searchQuery = ''
        return
      }

      // Create denormalized copy of the ingredient data
      const seasonalIngredient = {
        id: `${this.monthId}_${Date.now()}`,
        ingredient_id: ingredient.id, // Reference to original
        name: ingredient.name,
        icon_url: ingredient.icon_url || null,
        icon_color: ingredient.icon_color || '#7CB342',
        search_term: ingredient.name.toLowerCase()
      }

      this.ingredients.push(seasonalIngredient)
      this.emitUpdate()
      this.searchQuery = ''
      this.$nextTick(() => {
        this.$refs.ingredientSearch.newValue = ''
      })
    },
    async refreshIngredientsList() {
      const cloned = JSON.parse(JSON.stringify(this.ingredients))

      // Clear the list
      this.ingredients.splice(0, this.ingredients.length)

      // Re-fetch each ingredient from Firestore and re-add with fresh data
      for (const ing of cloned) {
        try {
          const snapshot = await db.collection('ingredients').doc(ing.ingredient_id).get()
          if (snapshot.exists) {
            const fresh = snapshot.data()
            this.ingredients.push({
              id: ing.id,
              ingredient_id: ing.ingredient_id,
              name: fresh.name,
              icon_url: fresh.icon_url || null,
              icon_color: fresh.icon_color || '#7CB342',
              search_term: ing.search_term // preserve any custom override
            })
          } else {
            console.log(`Warning: Ingredient ${ing.ingredient_id} does not exist - Skipping!`)
            this.$buefy.notification.open({ type: 'is-danger', message: `Warning: Ingredient "${ing.name}" no longer exists - Skipping!` })
          }
        } catch (error) {
          this.$buefy.notification.open({ type: 'is-danger', message: `Error fetching ingredient: ${error}` })
        }
      }

      this.emitUpdate()
      this.$buefy.toast.open({ type: 'is-success', message: 'Ingredients refreshed' })
    },
    removeIngredient(index) {
      this.$buefy.dialog.confirm({
        title: 'Remove Ingredient',
        message: 'Are you sure you want to remove this ingredient from the seasonal list?',
        confirmText: 'Remove',
        type: 'is-danger',
        onConfirm: () => {
          this.ingredients.splice(index, 1)
          this.emitUpdate()
        }
      })
    }
  }
}
</script>

<style scoped>
.ingredient-item {
  padding: 0.75rem;
}
.drag-ghost {
  opacity: 0.5;
  background: #f5f5f5;
}
.drag-item {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.icon-preview {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
}
.icon-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.icon-preview-small {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
}
.icon-preview-small img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
